/* ============================================================================
 * คำขอเติมเหรียญแบบโอนเงินเอง (manual top-up)
 *
 * ตัวเลือกกลางที่ใช้ร่วมกันสองที่ :
 *   กล่อง "เติมเหรียญ" ของพาร์ตเนอร์   (components/wallet/TopupModal.js)
 *   คิวตรวจฝั่งผู้ดูแล                  (page/admin/wallet_topups.js)
 *
 * ค่าคงที่ในไฟล์นี้ต้องตรงกับ modules/admin/wallet-topup/wallet-topup.constant.js
 * ฝั่ง backend — สถานะ ช่องทาง และเส้นทางการเปลี่ยนสถานะถูกบังคับซ้ำที่นั่นอีกชั้น
 *
 * อัตราแลกเปลี่ยน เพดานเติม และบัญชีรับเงิน ตั้งที่หน้า ตั้งค่า › ตั้งค่าเหรียญ
 * แล้วมาถึงหน้าเว็บพร้อมยอดเหรียญที่ GET admin/wallets/overview → data.coin
 *
 * ขั้นตอนของเรื่องนี้ :
 *   พาร์ตเนอร์โอนเงิน → กดปุ่ม "เติมเหรียญ" ข้างยอดเหรียญบนหัวหน้า → กรอกยอด
 *   แนบสลิป → คำขอเข้าคิวด้วยสถานะ "รอตรวจสอบ" → ทีมการเงินเทียบกับรายการเงินเข้า
 *   → อนุมัติ (เหรียญเข้ากระเป๋าเป็นรายการ topup) หรือไม่อนุมัติ (ต้องมีเหตุผล)
 *
 * เหรียญเข้ากระเป๋า "ตอนอนุมัติเท่านั้น" ไม่ใช่ตอนส่งคำขอ — สลิปปลอมหรือโอนไม่ถึง
 * จะได้ไม่กลายเป็นเหรียญที่ใช้ไปแล้วก่อนที่ใครจะทันตรวจ
 * ========================================================================== */

/* ------------------------------------------------------------------ สถานะ */
/* pending    รอตรวจสอบ — สถานะตั้งต้นของทุกคำขอ
   approved   อนุมัติ — เหรียญเข้ากระเป๋าแล้ว (ปลายทาง แก้ไม่ได้อีก)
   rejected   ไม่อนุมัติ — ต้องมีเหตุผลเสมอ (ปลายทาง)
   canceled   พาร์ตเนอร์ยกเลิกเอง — ทำได้เฉพาะตอนที่ยังไม่ถูกตัดสิน

   ไม่มีสถานะ "กำลังตรวจสอบ" โดยตั้งใจ — การเทียบสลิปกับรายการเงินเข้าจบในครั้งเดียว
   ไม่ใช่งานที่ค้างข้ามวันเหมือนการตรวจใบสมัคร การมีสถานะกลางจึงมีแต่เพิ่มขั้นให้กด

   tone/tint ใช้ชุดเดียวกับสถานะใบสมัครพาร์ตเนอร์ ผ่านการตรวจ contrast บนพื้นขาวแล้ว */
export const TOPUP_STATUSES = [
  { value: "pending", label: "topup_status_pending", icon: "\uf017", tone: "#b45309", tint: "#fef3c7" },
  { value: "approved", label: "topup_status_approved", icon: "\uf058", tone: "#15803d", tint: "#dcfce7" },
  { value: "rejected", label: "topup_status_rejected", icon: "\uf057", tone: "#b91c1c", tint: "#fee2e2" },
  { value: "canceled", label: "topup_status_canceled", icon: "\uf05e", tone: "#57534e", tint: "#e7e5e4" },
];

const UNKNOWN_STATUS = { value: "", label: "-", icon: "", tone: "#94a3b8", tint: "#e2e8f0" };

export const statusMeta = (value) => TOPUP_STATUSES.find((item) => item.value === value) || UNKNOWN_STATUS;

/* คำขอที่ยังค้างอยู่ในคิว — ป้ายตัวเลขบนเมนู "รายการเติมเหรียญ" นับจากชุดนี้
   (ตัวเลขจริงมาจาก summary.open ของ backend ไม่ได้นับที่หน้าเว็บ) */
export const OPEN_STATUSES = ["pending"];

// สถานะตั้งต้นของคำขอที่เพิ่งส่ง
export const DEFAULT_STATUS = "pending";

/* เปลี่ยนสถานะไปไหนได้บ้าง — ปลายทาง (approved/rejected/canceled) ไม่มีทางออก
   ย้อนกลับไม่ได้โดยตั้งใจ : อนุมัติแล้วเหรียญเข้ากระเป๋าและถูกใช้ต่อได้ทันที
   ถ้าตรวจทีหลังพบว่าผิด ให้ใช้การปรับยอดที่หน้าพาร์ตเนอร์ซึ่งมีร่องรอยของตัวเอง */
export const STATUS_TRANSITIONS = {
  pending: ["approved", "rejected"],
  approved: [],
  rejected: [],
  canceled: [],
};

// ไม่อนุมัติต้องบอกเหตุผลเสมอ — พาร์ตเนอร์เห็นข้อความนี้และใช้แก้ไขก่อนส่งใหม่
export const REASON_REQUIRED_STATUSES = ["rejected"];

// พาร์ตเนอร์ยกเลิกเองได้เฉพาะตอนที่ยังไม่มีใครรับเรื่องไปตรวจ
export const CANCELABLE_STATUSES = ["pending"];

/* ============================================================================
 * ชื่อสถานะที่ "พาร์ตเนอร์" เห็น — ไม่ใช่ชุดเดียวกับที่ฝั่งผู้ดูแลเห็น
 *
 * ฝั่งผู้ดูแลอ่านสถานะในฐานะผลของงานที่ตัวเองทำ : อนุมัติแล้ว / ไม่อนุมัติ
 * ฝั่งพาร์ตเนอร์อ่านในฐานะผลของเรื่องที่ตัวเองขอ : สำเร็จ / ไม่สำเร็จ
 * คำว่า "ไม่อนุมัติ" ในมือคนที่โอนเงินไปแล้วอ่านเหมือนถูกตัดสินว่าทำผิด ทั้งที่
 * ส่วนใหญ่เป็นเรื่องยอดไม่ตรงหรือสลิปไม่ชัด ซึ่งแก้แล้วส่งใหม่ได้
 *
 * ระบุเฉพาะสถานะที่ต้องใช้คำต่างกัน — รอตรวจสอบ/ยกเลิกแล้ว ใช้คำเดียวกันทั้งสองฝั่ง
 * อยู่แล้ว จึงตกไปที่ label เดิมของ statusMeta
 * ========================================================================== */
const PARTNER_STATUS_LABELS = {
  approved: "topup_status_done",
  rejected: "topup_status_failed",
};

export const partnerStatusLabel = (value) => PARTNER_STATUS_LABELS[value] || statusMeta(value).label;

/* ป้ายกำกับปุ่มตัดสิน — แยกจากชื่อสถานะ เพราะปุ่มเป็นคำสั่ง ("อนุมัติ")
   ส่วนสถานะเป็นผลลัพธ์ ("อนุมัติแล้ว") */
export const ACTION_META = {
  approved: { label: "topup_action_approve", icon: "\uf00c" },
  rejected: { label: "topup_action_reject", icon: "\uf00d" },
};

export const actionMeta = (to) => ACTION_META[to] || { label: "", icon: "" };

/* ------------------------------------------------------- ช่องทางการชำระเงิน */
export const PAYMENT_CHANNELS = [
  { value: "bank_transfer", label: "topup_channel_bank", icon: "\uf19c" },
  { value: "promptpay", label: "topup_channel_promptpay", icon: "\uf029" },
];

export const channelMeta = (value) =>
  PAYMENT_CHANNELS.find((item) => item.value === value) || { value: "", label: "-", icon: "" };

/* บัญชีปลายทางที่ให้พาร์ตเนอร์โอนเข้า
 *
 * มาจากค่าตั้งค่าในหน้า "ตั้งค่า › ตั้งค่าเหรียญ" (ตาราง wallet_settings) ส่งมาให้
 * พาร์ตเนอร์พร้อมยอดเหรียญที่ GET admin/wallets/overview → data.coin.payment
 * ไม่ใช่ค่าคงที่ในโค้ด เพราะบัญชีบริษัทเปลี่ยนได้ และคนที่รู้ว่าต้องเปลี่ยนเป็นเลขอะไร
 * คือฝ่ายการเงิน ไม่ใช่คนเขียนโปรแกรม
 *
 * ช่องทางไหนยังไม่ได้ตั้งเลขบัญชี จะไม่ถูกเสนอให้เลือกในฟอร์มเลย — ดีกว่าให้เลือกได้
 * แล้วไปเจอกล่องบัญชีว่างเปล่าตอนจะโอนจริง
 */
export const accountsFrom = (coin = {}) => {
  const payment = coin.payment || {};
  const list = [];

  if (String(payment.account_no || "").trim()) {
    list.push({
      channel: "bank_transfer",
      bank: payment.bank || "",
      account_no: payment.account_no,
      account_name: payment.account_name || "",
    });
  }

  if (String(payment.promptpay || "").trim()) {
    list.push({
      channel: "promptpay",
      bank: "",
      account_no: payment.promptpay,
      account_name: payment.account_name || "",
    });
  }

  return list;
};

export const accountFor = (coin, channel) => accountsFrom(coin).find((item) => item.channel === channel) || null;

/** ช่องทางที่ตั้งบัญชีปลายทางไว้แล้วเท่านั้น — ที่เหลือไม่ต้องเสนอให้เลือก */
export const channelsFrom = (coin) => {
  const ready = accountsFrom(coin).map((item) => item.channel);
  return PAYMENT_CHANNELS.filter((item) => ready.includes(item.value));
};

/** รหัสธนาคาร -> ชื่อที่คนอ่านออก (รายชื่ออยู่ที่ global.bank ใน components/global.js) */
export const bankLabel = (code, th = true) => {
  const bank = (global.bank || []).find((item) => item.value === code);
  if (!bank) return code || "";
  return th ? bank.name_th : bank.label;
};

/* โลโก้ธนาคาร — ไฟล์อยู่ที่ assets/images/bank ตั้งชื่อตรงกับฟิลด์ icon ของ global.bank
 *
 * ใช้ require.context ไม่ใช่ import ทีละไฟล์ เพราะชื่อไฟล์ถูกเลือกตอนทำงานจากค่าที่
 * ผู้ใช้ตั้งไว้ ไม่ใช่ค่าที่รู้ตั้งแต่ตอนเขียนโค้ด
 *
 * regex ระบุชื่อไฟล์ทีละอันแทนการรับ .png ทั้งโฟลเดอร์ เพราะในโฟลเดอร์มีโลโก้อยู่
 * เกือบเจ็ดสิบไฟล์ แต่รายชื่อธนาคารที่เปิดให้เลือกจริง (global.bank) มีสิบแปดราย
 * — เหมาทั้งโฟลเดอร์ทำให้ไฟล์ที่เล็กกว่า 10KB ถูกฝังเป็น base64 ลงใน bundle หลัก
 * ทั้งหมด (วัดแล้วบวมขึ้นราว 150KB gzip) ทั้งที่ห้าสิบกว่าไฟล์นั้นไม่มีใครเรียกใช้
 *
 * ⚠️ รายชื่อใน regex ต้องตรงกับ icon ของ global.bank — เพิ่มธนาคารที่นั่นแล้วต้อง
 *    มาเพิ่มที่นี่ด้วย ไม่งั้นตัวเลือกนั้นจะไม่มีโลโก้ (หน้าไม่พัง แต่รูปหาย)
 *
 * webpack ของ CRA คืนค่าเป็น { default: url } หรือ url ตรง ๆ แล้วแต่ชนิดโมดูล
 * จึงต้องรองรับทั้งสองแบบ
 */
const BANK_LOGOS = require.context(
  "../assets/images/bank",
  false,
  /^\.\/(BAAC|BAY|BBL|CIMB|GHB|GSB|HSBC|IBANK|ICBC|KBANK|KKP|KTB|LHB|SCB|TCRB|TISCO|TTB|UOB|PromptPay)\.png$/
);

const logoUrl = (file) => {
  if (!file) return "";
  try {
    const mod = BANK_LOGOS(`./${file}`);
    return mod?.default || mod || "";
  } catch {
    // ไม่มีไฟล์โลโก้ของธนาคารนั้น = ไม่โชว์รูป ไม่ใช่หน้าเว็บพัง
    return "";
  }
};

export const bankLogo = (code) => logoUrl((global.bank || []).find((item) => item.value === code)?.icon);

// พร้อมเพย์ไม่ได้อยู่ในรายชื่อธนาคาร แต่มีโลโก้ของตัวเองในโฟลเดอร์เดียวกัน
export const PROMPTPAY_LOGO = logoUrl("PromptPay.png");

/* ------------------------------------------------------------------ สลิป */
/* สลิปเป็นรูปถ่ายจากมือถือเป็นหลัก 5MB เหลือเฟือ และรับ PDF ด้วยเพราะแอปธนาคาร
   บางเจ้าให้ดาวน์โหลดหลักฐานเป็น PDF ไม่ใช่รูป */
export const SLIP_MAX_SIZE = 5 * 1024 * 1024;
export const SLIP_ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";

/* โฟลเดอร์ปลายทางบน R2 — ต้องตรงกับ SLIP_FOLDER ใน wallet-topup.constant.js
   ฝั่ง backend เพื่อให้สลิปทุกใบไปรวมอยู่ที่เดียวกัน ไม่กระจายตามใครเป็นคนอัป */
export const SLIP_FOLDER = "wallet/topup";

export const isImageSlip = (slip = {}) => String(slip.mime_type || "").startsWith("image/");

/* --------------------------------------------------------------- ยอดเงิน */
/* ปุ่มยอดที่ใช้บ่อย — กดแล้วเติมลงช่องเลย ไม่ต้องพิมพ์
   ไม่ใช่แพ็กเกจบังคับ ผู้ใช้ยังกรอกยอดเองได้ทุกจำนวน */
export const AMOUNT_PRESETS = [300, 500, 1000, 2000];

/* ------------------------------------------------------------ การเรียงลำดับ */
export const SORTS = [
  { value: "created_desc", label: "topup_sort_newest" },
  { value: "created_asc", label: "topup_sort_oldest" },
  { value: "amount_desc", label: "topup_sort_amount_high" },
  { value: "amount_asc", label: "topup_sort_amount_low" },
];

/* ============================================================================
 * ค่าสำรอง / โครงเปล่า
 * ========================================================================== */

/* ค่าสำรองของฟอร์มเติมเหรียญ — ใช้เฉพาะตอนที่ยังไม่ได้คำตอบจาก
   GET admin/wallets/overview (ยิงพลาด หรือฐานข้อมูลยังไม่ได้รัน migration ตั้งค่าเหรียญ)
   ค่าจริงมาจาก data.coin ของปลายทางนั้น ไม่ใช่ที่นี่ */
export const FALLBACK_COIN = {
  enabled: true,
  coin_name: "",
  coin_per_baht: 10,
  min_topup: 0,
  max_topup: 0,
  low_balance: 0,
  payment: {},
};

/* โครงเปล่าของการ์ดสรุป — ใช้ระหว่างรอ GET admin/wallet-topups/summary ตอบ
   การ์ดจึง render ได้โดยไม่ต้องเช็ก null ทีละใบ และไม่ขึ้น undefined ชั่วครู่ตอนเปิดหน้า
   คีย์ต้องตรงกับ toTopupSummaryResponse ฝั่ง backend */
export const EMPTY_SUMMARY = {
  total: 0,
  status: Object.fromEntries(TOPUP_STATUSES.map((item) => [item.value, 0])),
  open: 0,
  approved_amount: 0,
  approved_coin: 0,
};

/* --------------------------------------------------------------- ตัวช่วย */

/** จำนวนเงิน (บาท) ที่แสดงบนหน้าจอ — ทศนิยมโชว์เฉพาะตอนมีเศษจริง */
export const baht = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0";

  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  });
};
